import {
	QuestionBox,
	type QuestionBoxProps,
	type QuestionContextVideo,
} from "@ui/organisms/QuestionBox/QuestionBox";
import { VStack } from "@ui/Stack";
import { Show } from "solid-js";
import { type UseAskPageFormOptions, useAskPageForm } from "./useAskPageForm";
import "./AskPageForm.css";

type Props = UseAskPageFormOptions & {
	placeholder?: string;
	creatorName: string;
	/** Videos the thread has mentioned so far (pinned at the top of the picker). */
	contextVideos?: QuestionContextVideo[];
	influencerId: string;
	variant?: QuestionBoxProps["variant"];
	maxLines?: number;
	disabled?: boolean;
	onReady?: QuestionBoxProps["onReady"];
	/** Whether the visitor's own credits pay for the question; never for a guest. */
	paysOwnCredits?: boolean;
};

/**
 * Ask-page question form (Task 4.1b, tasks/ask-page-decoupling-plan.md).
 * Posts directly to POST /api/public/ask-page/{handle}/questions through
 * QuestionBox's `onAsk`, not ~/modules/shared/chat/chat-panel/startChat,
 * which is the authenticated conversational-streaming flow
 * (POST /api/questions) this page's guests can't reach anyway.
 */
export const AskPageForm = (props: Props) => {
	const {
		prompt,
		setPrompt,
		effort,
		setEffort,
		rememberEffort,
		status,
		error,
		disabledReason,
		submit,
	} = useAskPageForm(props);

	return (
		<VStack spacing="small">
			<QuestionBox
				scope="influencer_pool"
				contextTabs={["videos"]}
				contextSource="pool"
				contextCreators={{ influencerId: props.influencerId }}
				contextVideos={props.contextVideos}
				askPageHandle={props.handle}
				askPageVideoSearch={props.isSignedIn ? "authenticated" : "public"}
				mentions={props.contextVideos !== undefined}
				chargesViewerCredits={props.paysOwnCredits === true}
				redirectURL=""
				question={prompt()}
				onQuestionChange={setPrompt}
				placeholder={props.placeholder ?? "Ask a question…"}
				variant={props.variant}
				maxLines={props.maxLines}
				buttonLabel="Ask"
				buttonSymbol="chevron_right"
				disabled={props.disabled || status() === "submitting"}
				// Held back while the thread disables the box, so it appears as
				// the box would otherwise come back on, not mid-answer.
				disabledReason={props.disabled ? undefined : disabledReason()}
				isLoading={status() === "submitting"}
				onAsk={(boxPrompt) => void submit(boxPrompt)}
				creators={[{ handle: props.handle, displayName: props.creatorName }]}
				effort={effort()}
				rememberEffort={rememberEffort()}
				onEffortChange={setEffort}
				onReady={props.onReady}
			/>
			<Show when={error()}>
				<span class="ask-page__error">{error()}</span>
			</Show>
		</VStack>
	);
};
